import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Modal, Field, inputCls, money, Pill, useDirectory } from "./common";

const empty = { customer_id: "", scope: "", materials: "", labor_cost: "", materials_cost: "", total: "", expiration_date: "", status: "draft" };

export default function EstimatesManager() {
  const [items, setItems] = useState(null);
  const [modal, setModal] = useState(null);
  const { customers, custName } = useDirectory();

  const load = useCallback(async () => {
    try { const { data } = await api.get("/estimates"); setItems(data); } catch { setItems([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setModal((m) => ({ ...m, [k]: v }));
  const save = async () => {
    try { await api.post("/estimates", modal); toast.success("Estimate created"); setModal(null); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  const setStatus = async (id, status) => {
    try { await api.put(`/estimates/${id}`, { status }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  return (
    <div data-testid="estimates-manager">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-display text-2xl font-600 text-charcoal">Estimates</h2>
        <button onClick={() => setModal({ ...empty })} data-testid="new-estimate-btn" className="inline-flex items-center gap-2 bg-emerald text-white px-4 py-2.5 rounded-sm text-sm font-600 hover:bg-emerald-dark"><Plus className="w-4 h-4" /> New Estimate</button>
      </div>
      {items === null ? <Loader2 className="w-5 h-5 animate-spin text-olive" /> : items.length === 0 ? (
        <p className="text-olive" data-testid="estimates-empty">No estimates yet.</p>
      ) : (
        <div className="space-y-3">
          {items.map((e) => (
            <div key={e.id} className="bg-white border border-border rounded-sm p-5" data-testid={`estimate-${e.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2"><span className="font-600 text-charcoal">{custName(e.customer_id)}</span><Pill status={e.status} /></div>
                  <p className="text-olive text-sm mt-1">{e.scope}</p>
                  <p className="text-charcoal font-600 mt-1">{money(e.total)}{e.expiration_date ? ` · expires ${e.expiration_date}` : ""}</p>
                </div>
                <select className="border border-border rounded-sm px-2 py-1.5 text-sm" data-testid={`estimate-status-${e.id}`} value={e.status} onChange={(ev) => setStatus(e.id, ev.target.value)}>
                  {["draft", "sent", "approved", "declined", "expired"].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
          ))}
        </div>
      )}
      {modal && (
        <Modal title="New Estimate" onClose={() => setModal(null)} testid="estimate-modal">
          <Field label="Customer"><select className={inputCls} data-testid="estimate-customer" value={modal.customer_id} onChange={(e) => set("customer_id", e.target.value)}><option value="">Select…</option>{customers.map((c) => <option key={c.id} value={c.id}>{c.name || c.email}</option>)}</select></Field>
          <Field label="Scope of Work"><textarea rows={2} className={inputCls} data-testid="estimate-scope" value={modal.scope} onChange={(e) => set("scope", e.target.value)} /></Field>
          <Field label="Materials"><input className={inputCls} value={modal.materials} onChange={(e) => set("materials", e.target.value)} /></Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Labor $"><input type="number" className={inputCls} value={modal.labor_cost} onChange={(e) => set("labor_cost", e.target.value)} /></Field>
            <Field label="Materials $"><input type="number" className={inputCls} value={modal.materials_cost} onChange={(e) => set("materials_cost", e.target.value)} /></Field>
            <Field label="Total $"><input type="number" className={inputCls} data-testid="estimate-total" value={modal.total} onChange={(e) => set("total", e.target.value)} /></Field>
          </div>
          <Field label="Expiration Date"><input type="date" className={inputCls} value={modal.expiration_date} onChange={(e) => set("expiration_date", e.target.value)} /></Field>
          <Field label="Status"><select className={inputCls} value={modal.status} onChange={(e) => set("status", e.target.value)}>{["draft", "sent", "approved"].map((s) => <option key={s}>{s}</option>)}</select></Field>
          <button onClick={save} data-testid="estimate-save-btn" className="bg-emerald text-white px-5 py-2.5 rounded-sm font-600 hover:bg-emerald-dark">Save</button>
        </Modal>
      )}
    </div>
  );
}
